'use client';

import { FieldPath, FieldValues } from 'react-hook-form';
import { useLocale } from 'next-intl';
import {
  PhoneInput,
  defaultCountries,
  parseCountry
} from 'react-international-phone';
import 'react-international-phone/style.css';

const EXCLUDED_COUNTRY_ISO2 = new Set(['il']);
const COUNTRIES = defaultCountries.filter(
  (c) => !EXCLUDED_COUNTRY_ISO2.has(parseCountry(c).iso2)
);
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { cn } from '@/lib/utils';
import { BaseFormFieldProps } from '@/types/base-form';

interface FormPhoneInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> extends BaseFormFieldProps<TFieldValues, TName> {
  placeholder?: string;
  defaultCountry?: string;
}

function FormPhoneInput<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  control,
  name,
  label,
  description,
  required,
  placeholder,
  defaultCountry = 'om',
  disabled,
  className
}: FormPhoneInputProps<TFieldValues, TName>) {
  const locale = useLocale();
  const isRtl = locale === 'ar';

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          {label && (
            <FormLabel>
              {label}
              {required && <span className='ms-1 text-red-500'>*</span>}
            </FormLabel>
          )}
          <FormControl>
            <div dir='ltr'>
              <PhoneInput
                value={field.value ?? ''}
                onChange={field.onChange}
                defaultCountry={defaultCountry}
                countries={COUNTRIES}
                placeholder={placeholder}
                disabled={disabled}
                inputClassName={cn(
                  'flex h-9 w-full border border-input bg-transparent px-3 py-1 text-sm shadow-xs transition-colors',
                  'placeholder:text-muted-foreground',
                  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                  isRtl ? 'rounded-s-md' : 'rounded-e-md'
                )}
                countrySelectorStyleProps={{
                  buttonClassName: cn(
                    'flex h-9 items-center gap-1 border border-input bg-transparent px-2 text-sm',
                    'hover:bg-accent transition-colors',
                    isRtl
                      ? 'rounded-e-md border-s-0'
                      : 'rounded-s-md border-e-0'
                  )
                }}
                className='flex'
              />
            </div>
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export { FormPhoneInput };
